// src/services/customer/CustomerContactService.ts

import bcrypt from 'bcryptjs';
import { supabase } from '../../shared/services/supabase';
import { netlifyFetch } from '../../shared/services/netlifyFetch';
import { CustomerContact, CustomerContactCreateInput, CustomerContactUpdateInput } from '../../shared/types';

export class CustomerContactService {

  // ============================================
  // PRIVATE HELPER METHODEN
  // ============================================

  /**
   * Generiert einen kryptographisch sicheren Token
   * 64 Hex-Zeichen = 256 Bit Entropie (nicht erratbar)
   */
  private generateToken(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Hasht ein Passwort mit bcrypt
   */
  private async hashPassword(passwort: string): Promise<string> {
    const salt = await bcrypt.genSalt(10);
    return bcrypt.hash(passwort, salt);
  }

  // ============================================
  // CRUD METHODEN
  // ============================================

  /**
   * Lädt alle aktiven Ansprechpartner für einen Kunden
   */
  async getContactsForCustomer(customerId: string): Promise<CustomerContact[]> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true })
        .order('is_primary', { ascending: false }); // Primärer zuerst

      if (error) {
        console.error('Error loading customer contacts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getContactsForCustomer:', error);
      return [];
    }
  }

  /**
   * Lädt alle Ansprechpartner für einen Kunden (inkl. inaktive)
   */
  async getAllContactsForCustomer(customerId: string): Promise<CustomerContact[]> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', customerId)
        .order('sort_order', { ascending: true })
        .order('is_primary', { ascending: false });

      if (error) {
        console.error('Error loading all customer contacts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getAllContactsForCustomer:', error);
      return [];
    }
  }

  /**
   * Lädt einen einzelnen Ansprechpartner
   */
  async getContact(contactId: string): Promise<CustomerContact | null> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('id', contactId)
        .single();

      if (error) {
        console.error('Error loading contact:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getContact:', error);
      return null;
    }
  }

  /**
   * Lädt den primären Ansprechpartner eines Kunden
   */
  async getPrimaryContact(customerId: string): Promise<CustomerContact | null> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_primary', true)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Error loading primary contact:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in getPrimaryContact:', error);
      return null;
    }
  }

  /**
   * Erstellt einen neuen Ansprechpartner
   */
  async createContact(input: CustomerContactCreateInput): Promise<CustomerContact | null> {
    try {
      // Hole die höchste sort_order für diesen Kunden
      const { data: maxOrderData } = await supabase
        .from('customer_contacts')
        .select('sort_order')
        .eq('customer_id', input.customer_id)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextSortOrder = maxOrderData && maxOrderData.length > 0
        ? (maxOrderData[0].sort_order || 0) + 1
        : 0;

      // Wenn is_primary gesetzt wird, andere Kontakte auf is_primary = false setzen
      if (input.is_primary) {
        await this.clearPrimaryFlag(input.customer_id);
      }

      // Passwort hashen falls vorhanden
      let hashedPassword: string | null = null;
      if (input.secure_download_passwort && input.secure_download_passwort.trim() !== '') {
        hashedPassword = await this.hashPassword(input.secure_download_passwort);
      }

      const { data, error } = await supabase
        .from('customer_contacts')
        .insert({
          customer_id: input.customer_id,
          vorname: input.vorname || null,
          nachname: input.nachname || null,
          email: input.email,
          email_cc: input.email_cc || null,
          anrede: input.anrede || null,
          geschlecht: input.geschlecht || null,
          rolle: input.rolle || null,
          is_primary: input.is_primary || false,
          receives_finalization_package: input.receives_finalization_package || false,
          receives_lohnschein_report: input.receives_lohnschein_report || false,
          receives_oppm_payslips_qr: input.receives_oppm_payslips_qr || false,
          receives_monatsuebersicht_qr: input.receives_monatsuebersicht_qr || false,
          secure_download_passwort: hashedPassword,
          sort_order: nextSortOrder
        })
        .select('*')
        .single();

      if (error) {
        console.error('Error creating contact:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in createContact:', error);
      return null;
    }
  }

  /**
   * Aktualisiert einen Ansprechpartner
   */
  async updateContact(contactId: string, input: CustomerContactUpdateInput): Promise<CustomerContact | null> {
    try {
      // Wenn is_primary gesetzt wird, zuerst den Kunden ermitteln und andere Kontakte anpassen
      if (input.is_primary === true) {
        const contact = await this.getContact(contactId);
        if (contact) {
          await this.clearPrimaryFlag(contact.customer_id, contactId);
        }
      }

      // Passwort nur hashen, wenn ein neues gesetzt wird
      const updateData: Record<string, unknown> = { ...input, updated_at: new Date().toISOString() };

      if (input.secure_download_passwort !== undefined) {
        if (input.secure_download_passwort && input.secure_download_passwort.trim() !== '') {
          updateData.secure_download_passwort = await this.hashPassword(input.secure_download_passwort);
        } else {
          updateData.secure_download_passwort = null;
        }
      }

      const { data, error } = await supabase
        .from('customer_contacts')
        .update(updateData)
        .eq('id', contactId)
        .select('*')
        .single();

      if (error) {
        console.error('Error updating contact:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error in updateContact:', error);
      return null;
    }
  }

  /**
   * Löscht einen Ansprechpartner (hard delete)
   */
  async deleteContact(contactId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('customer_contacts')
        .delete()
        .eq('id', contactId);

      if (error) {
        console.error('Error deleting contact:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteContact:', error);
      return false;
    }
  }

  // ============================================
  // PRIMÄR-VERWALTUNG
  // ============================================

  /**
   * Setzt is_primary bei allen Ansprechpartnern eines Kunden auf false
   * Optional: Einen bestimmten Kontakt ausschließen (der gerade als primär gesetzt wird)
   */
  private async clearPrimaryFlag(customerId: string, excludeContactId?: string): Promise<void> {
    try {
      let query = supabase
        .from('customer_contacts')
        .update({ is_primary: false })
        .eq('customer_id', customerId)
        .eq('is_primary', true);

      if (excludeContactId) {
        query = query.neq('id', excludeContactId);
      }

      const { error } = await query;
      if (error) {
        console.error('Error clearing primary flag:', error);
      }
    } catch (error) {
      console.error('Error in clearPrimaryFlag:', error);
    }
  }

  /**
   * Setzt einen Ansprechpartner als primär (und entfernt Primär bei allen anderen)
   */
  async setPrimary(contactId: string, customerId: string): Promise<boolean> {
    try {
      // Zuerst alle anderen auf false
      await this.clearPrimaryFlag(customerId);

      // Dann den gewünschten auf true
      const { error } = await supabase
        .from('customer_contacts')
        .update({ is_primary: true, updated_at: new Date().toISOString() })
        .eq('id', contactId);

      if (error) {
        console.error('Error setting primary contact:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in setPrimary:', error);
      return false;
    }
  }

  // ============================================
  // TOKEN & PASSWORT VERWALTUNG
  // ============================================

  /**
   * Stellt sicher, dass ein Ansprechpartner einen Download-Token hat.
   * Generiert einen neuen Token falls keiner existiert.
   */
  async ensureContactToken(contactId: string): Promise<{ success: boolean; token?: string; error?: string }> {
    try {
      const { data: contact, error: fetchError } = await supabase
        .from('customer_contacts')
        .select('secure_download_token')
        .eq('id', contactId)
        .single();

      if (fetchError) {
        return {
          success: false,
          error: `Ansprechpartner nicht gefunden: ${fetchError.message}`
        };
      }

      // Token existiert bereits
      if (contact.secure_download_token) {
        return {
          success: true,
          token: contact.secure_download_token
        };
      }

      // Neuen Token generieren
      const newToken = this.generateToken();

      const { error: updateError } = await supabase
        .from('customer_contacts')
        .update({ secure_download_token: newToken, updated_at: new Date().toISOString() })
        .eq('id', contactId);

      if (updateError) {
        return {
          success: false,
          error: `Token konnte nicht gespeichert werden: ${updateError.message}`
        };
      }

      console.log(`[CustomerContactService] Generated new token for contact ${contactId}`);

      return {
        success: true,
        token: newToken
      };

    } catch (error) {
      console.error('Error in ensureContactToken:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Prüft ob ein Ansprechpartner ein Download-Passwort gesetzt hat
   */
  async hasContactPassword(contactId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('secure_download_passwort')
        .eq('id', contactId)
        .single();

      if (error || !data) return false;

      return !!data.secure_download_passwort && data.secure_download_passwort.trim() !== '';
    } catch {
      return false;
    }
  }

  /**
   * Setzt ein neues Passwort für einen Ansprechpartner
   */
  async setContactPassword(contactId: string, passwort: string): Promise<boolean> {
    try {
      const hashedPassword = await this.hashPassword(passwort);

      const { error } = await supabase
        .from('customer_contacts')
        .update({
          secure_download_passwort: hashedPassword,
          updated_at: new Date().toISOString()
        })
        .eq('id', contactId);

      if (error) {
        console.error('Error setting contact password:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in setContactPassword:', error);
      return false;
    }
  }

  /**
   * Sendet eine Passwort-Einrichtungs-Einladung an einen Ansprechpartner
   * Wird manuell aus dem Intranet ausgelöst
   * Generiert Setup-Token und sendet E-Mail via Netlify Function
   */
  async sendPasswordSetupInvite(contactId: string, senderEmail?: string): Promise<{ success: boolean; error?: string }> {
    try {
      const response = await netlifyFetch('/.netlify/functions/send-password-setup-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_id: contactId,
          sender_email: senderEmail || undefined
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        return {
          success: false,
          error: result.error || 'Einladung konnte nicht gesendet werden.'
        };
      }

      console.log(`[CustomerContactService] Password setup invite sent for contact ${contactId} to ${result.sentTo}`);

      return {
        success: true
      };

    } catch (error) {
      console.error('Error in sendPasswordSetupInvite:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler'
      };
    }
  }

  /**
   * Prüft ob ein Ansprechpartner eine ausstehende Passwort-Einladung hat
   */
  async hasPasswordSetupPending(contactId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('password_setup_token, password_setup_token_expires_at')
        .eq('id', contactId)
        .single();

      if (error || !data) return false;

      if (!data.password_setup_token || !data.password_setup_token_expires_at) return false;

      // Prüfe ob Token noch gültig ist
      const expiresAt = new Date(data.password_setup_token_expires_at);
      return expiresAt > new Date();
    } catch {
      return false;
    }
  }

  // ============================================
  // BATCH METHODEN (für Deadline-Loading)
  // ============================================

  /**
   * Lädt Ansprechpartner für mehrere Kunden (für Batch-Loading im Deadline-View)
   */
  async getContactsForCustomers(customerIds: string[]): Promise<Record<string, CustomerContact[]>> {
    try {
      if (customerIds.length === 0) return {};

      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .in('customer_id', customerIds)
        .eq('is_active', true)
        .order('customer_id', { ascending: true })
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading contacts for customers:', error);
        return {};
      }

      // Gruppiere Contacts nach customer_id
      const groupedContacts: Record<string, CustomerContact[]> = {};
      (data || []).forEach(contact => {
        if (!groupedContacts[contact.customer_id]) {
          groupedContacts[contact.customer_id] = [];
        }
        groupedContacts[contact.customer_id].push(contact);
      });

      return groupedContacts;
    } catch (error) {
      console.error('Error in getContactsForCustomers:', error);
      return {};
    }
  }

  /**
   * Lädt Ansprechpartner mit receives_finalization_package = true für einen Kunden
   */
  async getFinalizationContacts(customerId: string): Promise<CustomerContact[]> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .eq('receives_finalization_package', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading finalization contacts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getFinalizationContacts:', error);
      return [];
    }
  }

  /**
   * Lädt Ansprechpartner mit receives_lohnschein_report = true für einen Kunden
   * (Empfänger des Lohnschein-Sammelpakets für Arbeitnehmer ohne E-Mail oder IBAN, Stufe 3.2)
   */
  async getLohnscheinReportContacts(customerId: string): Promise<CustomerContact[]> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .eq('receives_lohnschein_report', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading lohnschein report contacts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getLohnscheinReportContacts:', error);
      return [];
    }
  }

  /**
   * Lädt Ansprechpartner mit receives_oppm_payslips_qr = true für einen Kunden
   * (Empfänger der OPPM Gehaltsdokumente mit QR-Code)
   */
  async getOppmPayslipsQrContacts(customerId: string): Promise<CustomerContact[]> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .eq('receives_oppm_payslips_qr', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading oppm payslips qr contacts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getOppmPayslipsQrContacts:', error);
      return [];
    }
  }

  /**
   * Lädt Ansprechpartner mit receives_monatsuebersicht_qr = true für einen Kunden
   * (Empfänger der OPPM Monatsübersichten mit QR-Code)
   */
  async getMonatsuebersichtQrContacts(customerId: string): Promise<CustomerContact[]> {
    try {
      const { data, error } = await supabase
        .from('customer_contacts')
        .select('*')
        .eq('customer_id', customerId)
        .eq('is_active', true)
        .eq('receives_monatsuebersicht_qr', true)
        .order('sort_order', { ascending: true });

      if (error) {
        console.error('Error loading monatsuebersicht qr contacts:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error in getMonatsuebersichtQrContacts:', error);
      return [];
    }
  }

  // ============================================
  // HILFSMETHODEN
  // ============================================

  /**
   * Gibt den vollständigen Namen eines Ansprechpartners zurück
   */
  getContactDisplayName(contact: CustomerContact): string {
    const parts: string[] = [];
    if (contact.vorname) parts.push(contact.vorname);
    if (contact.nachname) parts.push(contact.nachname);
    
    if (parts.length === 0) return contact.email;
    
    return parts.join(' ');
  }

  /**
   * Gibt den Anzeigenamen mit Rolle zurück
   */
  getContactDisplayNameWithRole(contact: CustomerContact): string {
    const name = this.getContactDisplayName(contact);
    if (contact.rolle) {
      return `${name} (${contact.rolle})`;
    }
    return name;
  }
}

// Singleton instance
export const customerContactService = new CustomerContactService();
